// DJB-279: the year › month › day tree of the inbox lists, built client-side
// from the day summary of GET /webforms/{kind}/days ([{ date, open,
// completed }]), and the rules for what starts open or collapsed. Pure
// functions so the grouping and the defaults can be unit-tested without the
// component.

export const MONTH_NAMES_NL = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December',
];

// Only the most recent months of a year are listed until "Show all ›"
export const DEFAULT_MONTHS_SHOWN = 3;

export const yearOf = (date) => String(date || '').slice(0, 4);
export const monthOf = (date) => String(date || '').slice(0, 7); // 'yyyy-MM'

// 'yyyy-MM' -> Dutch month name ("Oktober")
export const monthLabel = (month) => MONTH_NAMES_NL[Number(month.slice(5, 7)) - 1] || month;

// Keys of the open/collapsed state; one set carries all three levels
export const yearKey = (year) => `y:${year}`;
export const monthKey = (month) => `m:${month}`;
export const dayKey = (date) => `d:${date}`;

const byDateDesc = (a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0);

// The days a filter has something to show for: To Do needs open requests,
// Completed needs completed ones, All lists every day. The counts on the
// headers always come from the full summary, so they do not move with the
// filter (only the cards do).
export const daysForStatus = (days, status) =>
  days.filter((d) => (status === 'open' ? d.open > 0 : status === 'handled' ? d.completed > 0 : true));

// [{ year, open, completed, months: [{ month, year, label, open, completed,
// total, days: [{ date, open, completed }] }] }], newest first on every level
export const groupDays = (days) => {
  const years = new Map();
  [...days].sort(byDateDesc).forEach((day) => {
    const year = yearOf(day.date);
    const month = monthOf(day.date);
    if (!years.has(year)) years.set(year, { year, open: 0, completed: 0, months: new Map() });
    const y = years.get(year);
    if (!y.months.has(month)) {
      y.months.set(month, { month, year, label: monthLabel(month), open: 0, completed: 0, total: 0, days: [] });
    }
    const m = y.months.get(month);
    const open = Number(day.open) || 0;
    const completed = Number(day.completed) || 0;
    m.days.push({ ...day, open, completed });
    m.open += open;
    m.completed += completed;
    m.total += open + completed;
    y.open += open;
    y.completed += completed;
  });
  return [...years.values()].map((y) => ({ ...y, months: [...y.months.values()] }));
};

// The tree a filter shows: grouped from the FULL summary (so the "X/Y
// completed" of a month and the badge of a day never move with the filter),
// then pruned to the days the filter has something for; months and years
// left without days disappear
export const treeForStatus = (days, status) =>
  groupDays(days)
    .map((year) => ({
      ...year,
      months: year.months
        .map((month) => ({ ...month, days: daysForStatus(month.days, status) }))
        .filter((month) => month.days.length > 0),
    }))
    .filter((year) => year.months.length > 0);

// The months of a year that are listed: the DEFAULT_MONTHS_SHOWN most recent
// ones, or all of them after "Show all ›"
export const shownMonths = (months, showAll) => (showAll ? months : months.slice(0, DEFAULT_MONTHS_SHOWN));

export const hasHiddenMonths = (months, showAll) => !showAll && months.length > DEFAULT_MONTHS_SHOWN;

// Defaults: the current year and month start open, earlier ones collapsed; a
// day starts open while it has open requests and collapsed once everything
// on it is completed
export const isYearOpenByDefault = (year, today) => year === yearOf(today);
export const isMonthOpenByDefault = (month, today) => month === monthOf(today);
export const isDayOpenByDefault = (day) => (Number(day.open) || 0) > 0;

// Every key of the tree, and the subset that starts open
export const treeKeys = (days) => {
  const keys = new Set();
  days.forEach((day) => {
    keys.add(yearKey(yearOf(day.date)));
    keys.add(monthKey(monthOf(day.date)));
    keys.add(dayKey(day.date));
  });
  return keys;
};

export const defaultOpenKeys = (days, today) => {
  const keys = new Set();
  days.forEach((day) => {
    const year = yearOf(day.date);
    const month = monthOf(day.date);
    if (isYearOpenByDefault(year, today)) keys.add(yearKey(year));
    if (isMonthOpenByDefault(month, today)) keys.add(monthKey(month));
    if (isDayOpenByDefault(day)) keys.add(dayKey(day.date));
  });
  return keys;
};

// The dates whose cards are on screen: the day is open and so are its month
// and year, and the month is not hidden behind "Show all ›"
export const visibleDates = (tree, open, showAllYears) => {
  const dates = [];
  tree.forEach((year) => {
    if (!open.has(yearKey(year.year))) return;
    shownMonths(year.months, showAllYears.has(year.year)).forEach((month) => {
      if (!open.has(monthKey(month.month))) return;
      month.days.forEach((day) => {
        if (open.has(dayKey(day.date))) dates.push(day.date);
      });
    });
  });
  return dates;
};
